import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, MessageBarActions, MessageBarBody, MessageBarTitle, Spinner, Toast, ToastBody, ToastTitle, useToastController } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import * as StatusFunctions from './ContractReviewStatus.Functions';
import { ContractReview } from '../../../../models/Entities/Contracts/ContractReview';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideProgress } from '../../../../theme/components/GuideProgress';
import { GuideLinkButton } from '../../../../theme/components/GuideButtons';
import { formatDateTime, isReviewActive } from '../ContractReview.Functions';
import { useAfterImportReview, useContractReviewPolling, useReviewCompletionToast } from '../ContractReview.Hooks';
import { useContractReviewStyles } from '../ContractReview.Styles';
import { ReviewStatusPill } from '../ContractReviewPills/ContractReviewPills.Component';

/**
 * Estado de las revisiones con IA en el widget de importación: la última revisión (estado, avance o resultado, "Ver
 * revisión"), el aviso de la revisión automática después de importar, la consulta cada 5 s mientras haya una activa y el
 * toast al terminar. El historial completo y el lanzamiento están en el drawer de "Revisar con IA".
 */
export const ContractReviewStatusComponent: React.FC<StatusFunctions.Props> = props => {
  const { t } = useTranslation();
  const styles = useContractReviewStyles();
  const navigate = useNavigate();
  const { dispatchToast } = useToastController('PrebillerToast');
  const { contractId, reviews } = props;

  const refresh = useCallback(() => props.GetContractReviews(contractId), [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (contractId) refresh();
  }, [contractId, refresh]);

  const afterImport = useAfterImportReview(props.lastImportProcess, reviews);
  useContractReviewPolling(reviews, afterImport.waiting, refresh);

  const goToReview = (review: Pick<ContractReview, 'id'>) => navigate(`/contracts/${contractId}/reviews/${review.id}`);

  // La revisión de la primera importación se aplica sola: se recarga lo que cambió en el contrato.
  const refreshAppliedContract = (review: ContractReview) => {
    props.GetContractAddendumsByContractId(contractId);
    props.GetContractParametrizationStatus(contractId);
    if (review.addendumId !== null) {
      props.GetAllContractStagesByAddendumId(contractId, review.addendumId);
      props.GetAllContractsConceptByAddendum(contractId, review.addendumId);
    }
  };

  const noticeTitle = (status: 'Completed' | 'Failed' | 'Applied', review: ContractReview) =>
    status === 'Applied'
      ? t('contractReview.toast.applied', { count: review.appliedCount })
      : status === 'Completed'
        ? t('contractReview.toast.completed')
        : t('contractReview.toast.failed');

  const notice = useReviewCompletionToast(reviews, ({ review, status }) => {
    if (status === 'Applied') refreshAppliedContract(review);
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <Button size="small" appearance="transparent" onClick={() => goToReview(review)}>
              {t('contractReview.toast.view')}
            </Button>
          }
        >
          {noticeTitle(status, review)}
        </ToastTitle>
        {status === 'Failed' && review.errorMessage && <ToastBody>{review.errorMessage}</ToastBody>}
      </Toast>,
      { intent: status === 'Failed' ? 'error' : 'success' }
    );
  });

  // La lista viene ordenada de la más nueva a la más vieja.
  const last = reviews[0];

  return (
    <div className={styles.section} data-testid="contract-review-status">
      {(afterImport.waiting || afterImport.review) && (
        <GuideMessageBar intent="info" data-testid="after-import-review">
          <MessageBarBody>
            <MessageBarTitle>{t('contractReview.panel.afterImport.title')}</MessageBarTitle>
            {afterImport.review ? (
              <span className={styles.row}>
                <ReviewStatusPill status={afterImport.review.status} />
              </span>
            ) : (
              <span className={styles.row}>
                <Spinner size="extra-tiny" />
                {t('contractReview.panel.afterImport.waiting')}
              </span>
            )}
          </MessageBarBody>
          {afterImport.review && !isReviewActive(afterImport.review) && (
            <MessageBarActions>
              <Button size="small" onClick={() => goToReview(afterImport.review!)}>
                {t('contractReview.history.view')}
              </Button>
            </MessageBarActions>
          )}
        </GuideMessageBar>
      )}

      {last && !(afterImport.review && afterImport.review.id === last.id) && (
        <div className={styles.section} data-testid="last-review">
          <div className={styles.spaceBetween}>
            <div className={styles.row}>
              <span className={styles.secondary}>{t('contractReview.panel.lastReview')}</span>
              <ReviewStatusPill status={last.status} />
              {StatusFunctions.isAfterImportWithoutChanges(last) && (
                <span className={styles.secondary} data-testid="no-changes-to-apply">{t('contractReview.panel.noChangesToApply')}</span>
              )}
              <span className={styles.secondary}>{formatDateTime(last.created)}</span>
            </div>
            {!isReviewActive(last) && last.status !== 'Failed' && (
              <GuideLinkButton size="small" onClick={() => goToReview(last)}>
                {t('contractReview.history.view')}
              </GuideLinkButton>
            )}
          </div>
          {isReviewActive(last) && (
            <GuideProgress
              size="small"
              value={last.progressPercent}
              label={last.stage ? t(`contractReview.stage.${last.stage}`, last.stage) : t('contractReview.status.Queued')}
              ariaLabel={t('contractReview.history.progress', { value: last.progressPercent })}
            />
          )}
          {last.status === 'Failed' && last.errorMessage && <span className={styles.error}>{last.errorMessage}</span>}
        </div>
      )}

      <div aria-live="polite" role="status" className={styles.srOnly}>
        {notice ? noticeTitle(notice.status, notice.review) : ''}
      </div>
    </div>
  );
};

export default connect(StatusFunctions.mapStateToProps, StatusFunctions.mapDispatchToProps)(
  ContractReviewStatusComponent as any
) as unknown as React.FC<StatusFunctions.IProps>;
